"use client";

import React from "react";

import { isDraftDocument } from "lib/draft-utils";
import { useDraftMode } from "lib/use-draft-mode";

import { Chip } from "./chip";

interface DraftIndicatorProps {
  /**
   * The document ID (used to detect draft status).
   */
  _id?: string;
  /**
   * Whether the document is a draft (set by server when in draft mode).
   */
  _isDraft?: boolean;
  /**
   * Whether to render inline (just the chip) or wrapped in a container div.
   * Defaults to false (wrapped).
   */
  inline?: boolean;
  /**
   * The translations for the draft indicator.
   */
  translations: {
    draft: string;
  };
}

/**
 * Client component that displays a draft indicator chip when in draft mode
 * and the document is a draft. This is a small wrapper to avoid making
 * large parent components client components.
 */
export const DraftIndicator: React.FC<DraftIndicatorProps> = ({
  _id,
  _isDraft,
  inline = false,
  translations,
}) => {
  const t = translations;
  const isDraftMode = useDraftMode();
  const isDraft = isDraftDocument(_id, _isDraft);
  const showDraftIndicator = isDraftMode && isDraft;

  if (!showDraftIndicator) return null;

  const chip = <Chip className="h-fit" color="pink" label={t.draft} />;

  if (inline) {
    return chip;
  }

  return (
    <div className="flex flex-row flex-wrap items-center gap-4">{chip}</div>
  );
};
